import { IconProps, Portal } from '@chakra-ui/react'
import React, { MouseEventHandler, useId, useState } from 'react'
import { flushSync } from 'react-dom'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'
import { Popover, PopoverAnchor } from '~/components/ui/popover'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'

import { Menu, MenuOptions, MenuPopoverWrapper } from './Menu'
import { MenuItem } from './MenuItem'

export type MangoButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type DropdownActionButtonProps<TValue> = {
  variant: QuickActionButtonVariants
  size?: QuickActionButtonSizes
  tooltipText: string | React.ReactNode
  /** Accessible name; defaults to the text content of `tooltipText`. */
  ariaLabel?: string
  icon: React.ComponentType<any>
  onButtonClick?: () => void
  items: MangoButtonMenuItem<TValue>[]
  selectedIndex?: number
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => void
  menuOptions?: MenuOptions
  iconProps?: IconProps
}

export const DropdownActionButton = <TValue,>({
  variant,
  size,
  tooltipText,
  ariaLabel,
  icon,
  onButtonClick,
  items,
  selectedIndex,
  onSelect,
  onMenuOpenChange,
  menuOptions,
  iconProps,
}: DropdownActionButtonProps<TValue>) => {
  const [, setInternalIndex] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const buttonId = useId()

  useKeyPresses({
    onPress: (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
      }
    },
  })

  const handleMenuClose = () => {
    setMenuOpen(false)
    onMenuOpenChange?.(false)
  }
  useUserClickedExternalElements(
    [containerRef.current, buttonRef.current],
    true,
    handleMenuClose,
  )

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    onButtonClick?.()
    setMenuOpen((prev) => !prev)
    onMenuOpenChange?.(!menuOpen)
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setInternalIndex(index)
    setMenuOpen(false)
    onMenuOpenChange?.(false)
    buttonRef.current?.focus()
  }

  const handleKeyboardClose = () => {
    flushSync(() => setMenuOpen(false))
    onMenuOpenChange?.(false)
    buttonRef.current?.focus()
  }

  return (
    <Popover
      open={menuOpen}
      autoFocus={false}
      positioning={{ placement: 'bottom-start' }}
    >
      <PopoverAnchor>
        <QuickActionButton
          ref={buttonRef}
          id={buttonId}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          actionIcon={icon}
          tooltipText={tooltipText}
          ariaLabel={ariaLabel}
          tooltipDisabled={menuOpen}
          variant={variant}
          size={size}
          onClick={handleButtonClick}
          iconProps={iconProps}
        />
      </PopoverAnchor>
      {menuOpen && (
        <Portal>
          <MenuPopoverWrapper ref={containerRef}>
            <Menu
              items={items}
              onSelect={handleSelect}
              options={menuOptions}
              keyboard={{
                onClose: handleKeyboardClose,
                initialFocusIndex: selectedIndex ?? 0,
                labelledBy: buttonRef.current?.id || buttonId,
              }}
            />
          </MenuPopoverWrapper>
        </Portal>
      )}
    </Popover>
  )
}
